import { PatientHistoryType } from 'src/graphql.generated'

export type QuestionLabel = { key: string; label: string }

// Wording from Standard's `participantHistory/<Type>Fields.tsx`; array order is display order.
const PHQ9_QUESTIONS: QuestionLabel[] = [
  { key: 'PHQ9_QUESTION_1', label: 'Little interest or pleasure in doing things' },
  { key: 'PHQ9_QUESTION_2', label: 'Feeling down, depressed, or hopeless' },
  { key: 'PHQ9_QUESTION_3', label: 'Trouble falling or staying asleep, or sleeping too much' },
  { key: 'PHQ9_QUESTION_4', label: 'Feeling tired or having little energy' },
  { key: 'PHQ9_QUESTION_5', label: 'Poor appetite or overeating' },
  {
    key: 'PHQ9_QUESTION_6',
    label: 'Feeling bad about yourself, or that you are a failure, or have let yourself or your family down',
  },
  {
    key: 'PHQ9_QUESTION_7',
    label: 'Trouble concentrating on things, such as reading the newspaper or watching television',
  },
  {
    key: 'PHQ9_QUESTION_8',
    label:
      'Moving or speaking so slowly that other people could have noticed? Or the opposite, being so fidgety or restless that you have been moving around a lot more than usual',
  },
  { key: 'PHQ9_QUESTION_9', label: 'Thoughts that you would be better off dead or of hurting yourself in some way' },
  { key: 'PHQ9_SCORE', label: 'Score' },
]

const GAD7_QUESTIONS: QuestionLabel[] = [
  { key: 'GAD7_QUESTION_1', label: 'Feeling nervous, anxious or on edge?' },
  { key: 'GAD7_QUESTION_2', label: 'Not being able to stop or control worrying?' },
  { key: 'GAD7_QUESTION_3', label: 'Worrying too much about different things?' },
  { key: 'GAD7_QUESTION_4', label: 'Trouble relaxing?' },
  { key: 'GAD7_QUESTION_5', label: 'Being so restless that it is hard to sit still?' },
  { key: 'GAD7_QUESTION_6', label: 'Becoming easily annoyed or irritable?' },
  { key: 'GAD7_QUESTION_7', label: 'Feeling afraid as if something awful might happen?' },
  { key: 'GAD7_SCORE', label: 'Score' },
]

const WHO5_QUESTIONS: QuestionLabel[] = [
  { key: 'WHO5_QUESTION_1', label: 'I have felt cheerful and in good spirits.' },
  { key: 'WHO5_QUESTION_2', label: 'I have felt calm and relaxed.' },
  { key: 'WHO5_QUESTION_3', label: 'I have felt active and vigorous.' },
  { key: 'WHO5_QUESTION_4', label: 'I woke up feeling fresh and rested.' },
  { key: 'WHO5_QUESTION_5', label: 'My daily life has been filled with things that interest me.' },
  { key: 'WHO5_SCORE', label: 'Score' },
]

const ASRM_QUESTIONS: QuestionLabel[] = [
  { key: 'ASRM_QUESTION_1', label: 'Positive mood' },
  { key: 'ASRM_QUESTION_2', label: 'Self-confidence' },
  { key: 'ASRM_QUESTION_3', label: 'Sleep patterns' },
  { key: 'ASRM_QUESTION_4', label: 'Speech' },
  { key: 'ASRM_QUESTION_5', label: 'Activity level' },
  { key: 'ASRM_SCORE', label: 'Score' },
]

const ISI_QUESTIONS: QuestionLabel[] = [
  { key: 'ISI_QUESTION_1', label: 'Difficulty falling asleep' },
  { key: 'ISI_QUESTION_2', label: 'Difficulty staying asleep' },
  { key: 'ISI_QUESTION_3', label: 'Problems waking up too early' },
  { key: 'ISI_QUESTION_4', label: 'Satisfaction with current sleep pattern' },
  { key: 'ISI_QUESTION_5', label: 'How noticeable to others the sleep problem is' },
  { key: 'ISI_QUESTION_6', label: 'Worry or distress about the sleep problem' },
  { key: 'ISI_QUESTION_7', label: 'Interference with daily functioning' },
  { key: 'ISI_SCORE', label: 'Score' },
]

const OCIR_QUESTIONS: QuestionLabel[] = [
  { key: 'OCIR_QUESTION_1', label: 'I have saved up so many things that they get in the way' },
  { key: 'OCIR_QUESTION_2', label: 'I check things more often than necessary' },
  { key: 'OCIR_QUESTION_3', label: 'I get upset if objects are not arranged properly' },
  { key: 'OCIR_QUESTION_4', label: 'I feel compelled to count while I am doing things' },
  {
    key: 'OCIR_QUESTION_5',
    label: 'I find it difficult to touch an object when I know it has been touched by strangers or certain people',
  },
  { key: 'OCIR_QUESTION_6', label: 'I find it difficult to control my own thoughts' },
  { key: 'OCIR_QUESTION_7', label: 'I collect things I do not need' },
  { key: 'OCIR_QUESTION_8', label: 'I repeatedly check doors, windows, drawers, etc.' },
  { key: 'OCIR_QUESTION_9', label: 'I get upset if others change the way I have arranged things' },
  { key: 'OCIR_QUESTION_10', label: 'I feel I have to repeat certain numbers' },
  { key: 'OCIR_QUESTION_11', label: 'I sometimes have to wash or clean myself simply because I feel contaminated' },
  { key: 'OCIR_QUESTION_12', label: 'I am upset by unpleasant thoughts that come into my mind against my will' },
  { key: 'OCIR_QUESTION_13', label: 'I avoid throwing things away because I am afraid I might need them later' },
  { key: 'OCIR_QUESTION_14', label: 'I repeatedly check gas and water taps and light switches after turning them off' },
  { key: 'OCIR_QUESTION_15', label: 'I need things to be arranged in a particular order' },
  { key: 'OCIR_QUESTION_16', label: 'I feel that there are good and bad numbers' },
  { key: 'OCIR_QUESTION_17', label: 'I wash my hands more often and longer than necessary' },
  { key: 'OCIR_QUESTION_18', label: 'I frequently get nasty thoughts and have difficulty getting rid of them' },
  { key: 'OCIR_SCORE', label: 'Score' },
]

const PCL5_QUESTIONS: QuestionLabel[] = [
  { key: 'PCL5_QUESTION_1', label: 'Repeated, disturbing, and unwanted memories of the stressful experience' },
  { key: 'PCL5_QUESTION_2', label: 'Repeated, disturbing dreams of the stressful experience' },
  {
    key: 'PCL5_QUESTION_3',
    label: 'Suddenly feeling or acting as if the stressful experience were actually happening again',
  },
  { key: 'PCL5_QUESTION_4', label: 'Feeling very upset when something reminded you of the stressful experience' },
  {
    key: 'PCL5_QUESTION_5',
    label: 'Having strong physical reactions when something reminded you of the stressful experience',
  },
  { key: 'PCL5_QUESTION_6', label: 'Avoiding memories, thoughts, or feelings related to the stressful experience' },
  { key: 'PCL5_QUESTION_7', label: 'Avoiding external reminders of the stressful experience' },
  { key: 'PCL5_QUESTION_8', label: 'Trouble remembering important parts of the stressful experience' },
  { key: 'PCL5_QUESTION_9', label: 'Having strong negative beliefs about yourself, other people, or the world' },
  {
    key: 'PCL5_QUESTION_10',
    label: 'Blaming yourself or someone else for the stressful experience or what happened after it',
  },
  { key: 'PCL5_QUESTION_11', label: 'Having strong negative feelings such as fear, horror, anger, guilt, or shame' },
  { key: 'PCL5_QUESTION_12', label: 'Loss of interest in activities that you used to enjoy' },
  { key: 'PCL5_QUESTION_13', label: 'Feeling distant or cut off from other people' },
  { key: 'PCL5_QUESTION_14', label: 'Trouble experiencing positive feelings' },
  { key: 'PCL5_QUESTION_15', label: 'Irritable behavior, angry outbursts, or acting aggressively' },
  { key: 'PCL5_QUESTION_16', label: 'Taking too many risks or doing things that could cause you harm' },
  { key: 'PCL5_QUESTION_17', label: 'Being "superalert" or watchful or on guard' },
  { key: 'PCL5_QUESTION_18', label: 'Feeling jumpy or easily startled' },
  { key: 'PCL5_QUESTION_19', label: 'Having difficulty concentrating' },
  { key: 'PCL5_QUESTION_20', label: 'Trouble falling or staying asleep' },
  { key: 'PCL5_SCORE', label: 'Score' },
]

// WPAI has no single question list, so it stays on `DefaultBody`.
export const SUM_SCORED_QUESTIONS: Partial<Record<PatientHistoryType, QuestionLabel[]>> = {
  [PatientHistoryType.Phq9]: PHQ9_QUESTIONS,
  [PatientHistoryType.Gad7]: GAD7_QUESTIONS,
  [PatientHistoryType.Who5]: WHO5_QUESTIONS,
  [PatientHistoryType.Asrm]: ASRM_QUESTIONS,
  [PatientHistoryType.Isi]: ISI_QUESTIONS,
  [PatientHistoryType.Ocir]: OCIR_QUESTIONS,
  [PatientHistoryType.Pcl5]: PCL5_QUESTIONS,
}
